import { IModuleSchema, ModuleService } from '@/api/module';
import { IProjectSchema } from '@/api/project';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: {
  project?: IProjectSchema;
  value?: IModuleSchema[];
  selectedModule: { [x: number]: IModuleSchema };
  archivedModules?: IModuleSchema[];
} = {
  project: undefined,
  value: undefined,
  selectedModule: {},
  archivedModules: [],
};

const findModuleById = (
  modulesArray: IModuleSchema[],
  id: string,
): IModuleSchema | undefined => {
  let foundModule = undefined;

  // Loop melalui setiap module dalam array
  for (const module of modulesArray) {
    if (module.id === id) {
      foundModule = module; // Jika ID cocok, kembalikan module
      break;
    }

    // Jika ada submodule, lakukan pencarian secara rekursif
    if (module.submodule && module.submodule.length > 0) {
      foundModule = findModuleById(module.submodule, id);
      if (foundModule) {
        break; // Hentikan pencarian jika module ditemukan
      }
    }
  }

  return foundModule;
};

const updateOrderById = (
  modules: IModuleSchema[],
  id: string,
  newOrder: number,
): void => {
  for (const module of modules) {
    if (module.id === id) {
      module.order = newOrder;
    }

    if (module.submodule && module.submodule.length > 0) {
      updateOrderById(module.submodule, id, newOrder);
    }
  }
};

const updateParentById = (
  modules: IModuleSchema[],
  id: string,
  parentId: string | null,
): void => {
  for (const module of modules) {
    if (module.id === id) {
      module.parent_id = parentId;
    }

    if (module.submodule && module.submodule.length > 0) {
      updateParentById(module.submodule, id, parentId);
    }
  }
};

const sortModulesByOrder = (modules: IModuleSchema[]): IModuleSchema[] => {
  const updateOrderByIndex = (modules: IModuleSchema[]): IModuleSchema[] => {
    modules.forEach((module, index) => {
      module.order = index;

      if (module.submodule && module.submodule.length > 0) {
        updateOrderByIndex(module.submodule);
      }
    });

    return modules;
  };

  // sort logic
  // modules.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  modules.sort((a, b) => {
    if (a.module_name === "All Module") return -1;
    if (b.module_name === "All Module") return 1;
    return (a.module_name ?? '').localeCompare(b.module_name ?? '');
  });

  modules.forEach(module => {
    if (module.submodule && module.submodule.length > 0) {
      sortModulesByOrder(module.submodule);
    }
  });

  return updateOrderByIndex(modules);
};

const removeModuleById = (modules: IModuleSchema[], idToRemove: string) => {
  let removedModule = null; // Menyimpan module yang dihapus

  // Filter array module untuk menghapus module dengan ID yang cocok
  modules = modules.filter(module => {
    if (module.id === idToRemove) {
      removedModule = module; // Simpan module yang dihapus
      return false; // Hapus module dengan ID yang cocok
    }
    return true;
  });

  // Loop untuk mencari di dalam submodule dari setiap module yang tersisa
  modules.forEach(module => {
    if (module.submodule && module.submodule.length > 0) {
      // Panggil fungsi ini secara rekursif untuk submodule
      const result = removeModuleById(module.submodule, idToRemove);
      module.submodule = result.modules;
      if (result.removedModule) {
        removedModule = result.removedModule; // Ambil module yang dihapus dari submodule
      }
    }
  });

  return { modules, removedModule }; // Kembalikan module yang dihapus
};

// Fungsi untuk menambahkan module baru di tingkat atas
const addModule = (modules: IModuleSchema[], newModule: IModuleSchema) => {
  modules.push(newModule);
  return modules;
};

// Fungsi untuk memindahkan submodule menjadi parent baru
const moveSubmoduleToParent = (
  modules: IModuleSchema[],
  submoduleId: string,
) => {
  // 1. Hapus submodule dari parent lama
  const { modules: updatedModules, removedModule } = removeModuleById(
    modules,
    submoduleId,
  );

  if (removedModule) {
    // 2. Tambahkan submodule yang dihapus sebagai module baru di tingkat atas
    return addModule(updatedModules, removedModule);
  } else {
    console.log('Submodule dengan ID tersebut tidak ditemukan');
    return modules;
  }
};

// Fungsi untuk menambahkan submodule ke parent baru
const addSubmoduleById = (
  modules: IModuleSchema[],
  parentId: string,
  newSubmodule: IModuleSchema,
) => {
  // Loop melalui setiap module dalam array
  modules.forEach(module => {
    // Jika module atau submodule memiliki ID yang sesuai dengan parentId, tambahkan submodule baru
    if (module.id === parentId) {
      if (!module.submodule) {
        module.submodule = [];
      }
      module.submodule.push(newSubmodule); // Tambahkan submodule baru
    } else if (module.submodule && module.submodule.length > 0) {
      // Jika module memiliki submodule, panggil fungsi ini secara rekursif untuk submodule tersebut
      addSubmoduleById(module.submodule, parentId, newSubmodule);
    }
  });

  return modules;
};

// Fungsi untuk memindahkan submodule dari satu parent ke parent lain
const moveSubmodule = (
  modules: IModuleSchema[],
  submoduleId: string,
  newParentId: string,
) => {
  // 1. Hapus submodule dari parent lama
  const { modules: updatedModules, removedModule } = removeModuleById(
    modules,
    submoduleId,
  );

  if (removedModule) {
    // 2. Tambahkan submodule yang dihapus ke parent baru
    return addSubmoduleById(updatedModules, newParentId, removedModule);
  } else {
    console.log('Submodule dengan ID tersebut tidak ditemukan');
    return modules;
  }
};

const moduleSlice = createSlice({
  name: 'module',
  initialState,
  reducers: {
    init: (
      state,
      action: PayloadAction<{
        modules: IModuleSchema[];
        project?: IProjectSchema;
      }>,
    ) => {
      const { modules, project } = action.payload;

      if (project && !state.project) {
        state.value = modules;
      }

      if (project && state.project && project.id !== state.project.id) {
        state.value = modules;
      }

      state.project = project;
    },
    setSelectedModule: (
      state,
      action: PayloadAction<{ module: IModuleSchema; level: number }>,
    ) => {
      const copy = { ...state.selectedModule };
      copy[action.payload.level] = action.payload.module;

      state.selectedModule = copy;
    },
    removeSelectedModule: (state, action: PayloadAction<{ level: number }>) => {
      const copy = { ...state.selectedModule };
      const curLength = Object.keys(state.selectedModule).length;

      if (curLength - 1 === action.payload.level) {
        // Jika modul yang ditutup adalah yang terakhir, cukup hapus modul tersebut
        delete copy[action.payload.level];
      } else {
        // Jika modul yang ditutup bukan yang terakhir, hapus modul tersebut dan semua setelahnya
        for (let index = action.payload.level; index < curLength; index++) {
          delete copy[index];
        }
      }

      state.selectedModule = copy;
    },
    resetSelectedModule: state => {
      state.selectedModule = {};
    },
    updateModuleOrder: (
      state,
      action: PayloadAction<{
        level: number;
        dragIndex: number;
        hoverIndex: number;
      }>,
    ) => {
      const { dragIndex, hoverIndex, level } = action.payload;

      let moduleData = [];
      if (level === 0) {
        moduleData = [...(state.value ?? [])];
      } else {
        moduleData = [...(state.selectedModule[level - 1]?.submodule ?? [])];
      }

      if (moduleData.length === 0) return;

      const dragModule = moduleData[dragIndex];
      const hoverModule = moduleData[hoverIndex];

      dragModule.order = hoverIndex;
      hoverModule.order = dragIndex;

      if (level > 0) {
        updateOrderById(state.value ?? [], dragModule.id!, hoverIndex);
        updateOrderById(state.value ?? [], hoverModule.id!, dragIndex);

        const parent = { ...state.selectedModule[level - 1] };
        parent.submodule = [...sortModulesByOrder(moduleData)];
        state.selectedModule[level - 1] = parent;
      }

      // trigger to change state parent
      state.value = sortModulesByOrder(state.value ?? []);

      // update to server
      const { mutate } = ModuleService.updateSync();
      const updateModule1 = findModuleById(state.value, dragModule.id!);
      const updateModule2 = findModuleById(state.value, hoverModule.id!);

      mutate(updateModule1!);
      mutate(updateModule2!);
    },
    updateSubmoduleOrder: (
      state,
      action: PayloadAction<{
        toLevel: number;
        fromLevel: number;
        module: IModuleSchema;
      }>,
    ) => {
      const { toLevel, fromLevel, module } = action.payload;

      // jika tidak berpindah
      if (toLevel === fromLevel) return;

      let newState = [...(state.value ?? [])];

      // jika menjadi parent
      if (toLevel === 0) {
        newState = handleMoveToParent(state, newState, fromLevel, module);
      } else {
        newState = handleMoveToSubmodule(state, newState, toLevel, fromLevel, module);
      }

      // trigger to change state parent
      state.value = sortModulesByOrder(newState);

      // update to server
      const { mutate } = ModuleService.updateSync();
      const updatedModule = findModuleById(state.value, module.id!);
      mutate(updatedModule!);
    },

    // Fungsi untuk archive module dari state
    archiveModule: (
      state,
      action: PayloadAction<{ moduleId: string; isArchive: boolean }>
    ) => {
      const { moduleId, isArchive } = action.payload;
      if (isArchive) {
        const module = findModuleById(state.value ?? [], moduleId);
        if (module) {
          state.archivedModules?.push(module);
          state.value = state.value?.filter(module => module.id !== moduleId) ?? [];
        }
      } else {
        const module = findModuleById(state.archivedModules ?? [], moduleId);
        if (module) {
          module.is_archive = false;
          state.value = [...(state.value ?? []), module];
          state.archivedModules = state.archivedModules?.filter(m => m.id !== moduleId);
        }
      }
      state.value = sortModulesByOrder(state.value ?? []);
    },

    // Fungsi untuk delete module dari state
    deleteModule: (
      state,
      action: PayloadAction<{ moduleId: string }>
    ) => {
      const { moduleId } = action.payload;
      state.value = state.value?.filter(module => module.id !== moduleId) ?? [];
    },
  },
});

export const {
  init,
  setSelectedModule,
  removeSelectedModule,
  resetSelectedModule,
  updateModuleOrder,
  updateSubmoduleOrder,
  archiveModule,
  deleteModule,
} = moduleSlice.actions;
export const moduleReducer = moduleSlice.reducer;
const handleMoveToParent = (
  state: typeof initialState,
  newState: IModuleSchema[],
  fromLevel: number,
  module: IModuleSchema,
) => {
  const oldParent = state.selectedModule[fromLevel - 1];

  newState = moveSubmoduleToParent(newState, module.id!);

  if (oldParent) {
    updateParentById(newState, module.id!, null);

    const newSubmodule = findModuleById(newState, oldParent.id!);
    if (newSubmodule) state.selectedModule[fromLevel - 1] = newSubmodule;
  }

  return newState;
};

const handleMoveToSubmodule = (
  state: typeof initialState,
  newState: IModuleSchema[],
  toLevel: number,
  fromLevel: number,
  module: IModuleSchema,
) => {
  const oldParent = state.selectedModule[fromLevel - 1];
  const newParent = state.selectedModule[toLevel - 1];

  if (newParent.id === module.id) {
    return newState;
  }

  newState = moveSubmodule(newState, module.id!, newParent.id!);

  if (oldParent) {
    const newSubmodule = findModuleById(newState, oldParent.id!);
    updateParentById(newState, module.id!, oldParent.id!);

    if (newSubmodule) state.selectedModule[fromLevel - 1] = newSubmodule;
  }

  if (newParent) {
    const newSubmodule = findModuleById(newState, newParent.id!);
    updateParentById(newState, module.id!, newParent.id!);

    if (newSubmodule) state.selectedModule[toLevel - 1] = newSubmodule;
  }

  return newState;
};

export default moduleSlice;
